// ── 매입 행 표시 + 고르기 + 표에서 바로 쓰기 ─────────────────────────────────────
// - 2026-10-08 사용자 "기존꺼 수정이랑 일부 삭제": 맨 앞 체크칸으로 골라 [선택 삭제]. 전체 고르기 칸은 두지 않는다 —
//   많이 지울 땐 업로드 묶음 [삭제]를 쓴다(실수로 전부 지우기 방지).
// - 2026-11-03 사용자 "노션처럼": 표 위에 마우스를 올리면 맨 위에 ＋ 줄 → 누르면 빈 줄이 생기고 바로 적는다.
//   기존 줄도 칸을 누르면 그 자리에서 고친다(결정 2-b). Enter·다른 곳 누르기 = 저장, Esc = 취소(결정 3-a).
//   5차(사용자 '마우스 대면 뜨는 건 직관적이지 않다 — 빈 행 하나에 ＋ 버튼만', 결정 1b·2a·3b): 맨 위에 늘 빈 줄 하나,
//   맨 왼쪽 ＋ 를 누르면 그 빈 줄이 그대로 입력 줄이 된다(같은 높이라 아무것도 안 움직임). Esc·🗑 면 다시 빈 줄.
//   저장 뒤엔 목록이 새로 올 때까지 입력 줄을 둬서 사라졌다 다시 생기는 출렁임이 없다.
//   새 줄은 서버가 같은 거래(구분·사용일·업체·금액)를 보면 한 번 묻는다. 줄 끝 🗑 = 한 번 묻고 삭제(새 줄은 취소).
//   고치는 줄도 붙여넣기 창처럼 테두리 없는 한 줄 — 누른 칸만 파란 테두리(사용자 2차 요청, 예전 ↗ 수정 창은 없앰).
//   줄을 체크하거나 고치는 중이면 표 위 🎨 로 줄 전체 색(color_marks.row, 결정 4-b).
// - is_excluded=TRUE 였던 기존 행은 취소선·회색으로 표시하고 합계에서 제외.
import React, { useEffect, useRef, useState } from 'react';
import { stdTableWrap, stdTable, stdTh, stdTd, stdCell, stdOutlineBtn } from '../adminlayout/designStandard';
import { btnDanger } from '../revenuemgmt/styles';
import { COLS, fmtMoney, draftOf, isBlankDraft, draftChanged, rowColorBg } from './purchaseRowForm';
import PurchaseRowEditor from './PurchaseRowEditor';
import RowColorBar from './RowColorBar';
import TrashButton from './TrashButton';

const canHover = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(hover: hover)').matches : true;

export default function PurchaseTable({
  rows, selectedIds, onToggle, onClearSelection, onDeleteSelected, onDeleteRows, onCreate, onUpdate, onSetColor, notify,
  totalCount = null, onOpenAuditLog, // totalCount: 검색 중이면 전체 건수("보이는 / 전체"), 아니면 null
}) {
  const [active, setActive] = useState(null); // null | 'new' | 고치는 줄 id
  const [draft, setDraft] = useState(null);
  const [focusKey, setFocusKey] = useState(null);
  const [rowHeight, setRowHeight] = useState(null); // 고치기 전 그 줄 높이 — 고칠 때도 그대로(아래 줄이 안 움직이게)
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // 고치는 동안 칸 너비를 그 순간 그대로 묶는다 — 글자가 입력 칸으로 바뀌면 표가 너비를 다시 나눠 다른 줄 비고가 줄바꿈되며 움직여서
  const [colW, setColW] = useState(null);
  const theadRef = useRef(null);
  const addRowRef = useRef(null); // 맨 위 빈 줄(＋) — 누르면 이 높이 그대로 입력 줄이 된다
  const waitReload = useRef(false); // 저장 뒤 목록이 새로 올 때까지 입력 줄을 둔다
  const turn = useRef(0);        // 고치는 줄이 바뀔 때마다 +1 — 늦게 끝난 저장이 새로 연 줄을 닫지 않게
  const holdNew = useRef(false); // 새 줄을 저장 못 함(사용일·금액 없음 등) → 다른 줄로 못 옮기게 붙잡는다

  const totalAmount = rows.filter((r) => !r.is_excluded).reduce((s, r) => s + Number(r.amount || 0), 0);
  const excludedCount = rows.filter((r) => r.is_excluded).length;
  const picked = rows.filter((r) => selectedIds.has(r.id));
  const pickedAmount = picked.reduce((s, r) => s + Number(r.amount || 0), 0);
  const colorTargets = picked.length ? picked.map((r) => r.id) : (active != null && active !== 'new' ? [active] : []);

  function openRow(next, row, key, height = null) {
    if (holdNew.current) return;
    turn.current += 1;
    if (!colW) setColW([...(theadRef.current?.rows[0]?.cells || [])].map((th) => th.getBoundingClientRect().width));
    setActive(next);
    setDraft(draftOf(row));
    setFocusKey(key || null);
    setRowHeight(height);
    setError('');
  }
  function close() {
    turn.current += 1;
    holdNew.current = false;
    waitReload.current = false;
    setBusy(false);
    setColW(null);
    setActive(null);
    setDraft(null);
    setError('');
  }

  // 새 줄 = 빈 줄 높이 그대로(누를 때·취소할 때 아래 줄이 안 움직이게). 취소하면 다시 빈 줄
  const addNew = () => openRow('new', null, COLS[0].key, addRowRef.current?.getBoundingClientRect().height || null);

  async function commit() {
    if (busy || active == null) return;
    const my = turn.current;
    const isNew = active === 'new';
    const d = draft;
    let r;
    if (isNew) {
      if (isBlankDraft(d)) { close(); return; }
      if (!d.use_date || !String(d.amount).trim()) {
        holdNew.current = true;
        setError('사용일과 금액을 넣어야 저장돼요. (Esc = 취소)');
        return;
      }
      setBusy(true);
      r = await onCreate(d);
      if (r?.duplicate) {
        r = window.confirm(`${r.message}\n그래도 한 줄 더 넣을까요? (진짜 두 번 산 거래일 때)`)
          ? await onCreate(d, { allowDuplicate: true })
          : { ok: false, message: '넣지 않았습니다 — 같은 거래가 이미 있어요. (Esc = 취소)' };
      }
    } else {
      const row = rows.find((x) => x.id === active);
      if (!row || !draftChanged(row, d)) { close(); return; }
      setBusy(true);
      r = await onUpdate(active, d);
    }
    if (r?.ok) {
      if (turn.current !== my) { setBusy(false); return; }
      waitReload.current = true; // 입력은 막은 채로, 목록이 오면 닫는다(아래 effect)
      setTimeout(() => { if (waitReload.current && turn.current === my) close(); }, 3000);
      return;
    }
    setBusy(false);
    if (turn.current === my) {
      if (isNew) holdNew.current = true;
      setError(r?.message || '저장하지 못했습니다.');
    } else {
      notify?.(r?.message || '저장하지 못했습니다.', 'error');
    }
  }

  // Esc 는 커서가 어디 있든 고치기를 닫는다(확인 창이 닫힌 뒤엔 커서가 줄 밖이라 줄의 Esc 가 안 먹었다)
  useEffect(() => {
    if (active == null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  // close 는 ref·setter 만 써서 매번 같다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // 저장한 줄이 새 목록에 들어오면 입력 줄을 닫는다
  useEffect(() => { if (waitReload.current) close(); },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rows]);

  const paint = async (color) => { if (colorTargets.length) await onSetColor(colorTargets, color); };
  async function deleteRow(row) {
    const label = `${row.use_date} · ${row.vendor || '(업체 없음)'} · ${fmtMoney(row.amount)}원`;
    if (!window.confirm(`이 줄을 삭제할까요?\n${label}\n\n원본 시트에 이 줄이 남아 있으면 다음 붙여넣기 때 '신규'로 다시 들어옵니다.`)) return;
    if (active === row.id) close();
    await onDeleteRows([row.id]);
  }
  const checkbox = (row) => (
    <label style={checkLabel}>
      <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => onToggle(row.id)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
    </label>
  );
  const editor = (bg, lead, onTrash) => (
    <PurchaseRowEditor
      draft={draft} setDraft={setDraft} focusKey={focusKey} onCommit={commit} onCancel={close} onTrash={onTrash} rowHeight={rowHeight}
      cellBase={stdTd} bg={bg} error={error} busy={busy} lead={lead}
    />
  );

  return (
    <div>
      {/* 표 위 한 줄(늘 같은 높이) — 칸을 누르거나 체크해도 표가 밀리지 않게(2026-11-03 사용자 '왜 아래 칸들이 움직여?').
          왼쪽 합계, 오른쪽은 상황에 따라 안내 / 🎨 색 / 선택 합계·해제·삭제가 같은 자리에 바뀌어 나온다. */}
      <div style={toolbar}>
        <span>
          총 {totalCount != null ? `${rows.length} / ${totalCount}` : rows.length}건 · 매입 합계 (제외 제외) <strong style={{ color: 'var(--text)' }}>{fmtMoney(totalAmount)}원</strong>
          {excludedCount > 0 && <span style={{ marginLeft: 12, color: 'var(--text-muted)' }}>제외 {excludedCount}건</span>}
        </span>
        <div style={toolbarRight}>
          {picked.length > 0 && <span><strong>{picked.length}건</strong> 선택 · 합계 {fmtMoney(pickedAmount)}원</span>}
          <RowColorBar targetCount={colorTargets.length} onPick={paint} />
          {picked.length > 0 && <button style={stdOutlineBtn} onClick={onClearSelection}>선택 해제</button>}
          {picked.length > 0 && <button style={{ ...btnDanger, padding: '8px 14px', borderRadius: 12 }} onClick={() => onDeleteSelected(picked)}>선택 삭제</button>}
          {colorTargets.length === 0 && (
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {/* Esc 안내는 키보드가 있는 컴퓨터에만(2026-11-03 사용자 '나가기 esc 설명, 글씨 줄여줘' — 11) */}
              {canHover ? '칸을 누르면 바로 고칠 수 있어요 · Esc 누르면 나가기' : '칸을 누르면 바로 고칠 수 있어요'}
            </span>
          )}
          {/* 변경 이력(줄 고치기·지우기·색) — 사용자 그림: 표 위 줄 맨 오른쪽 */}
          {onOpenAuditLog && <button style={stdOutlineBtn} onClick={onOpenAuditLog}>이력</button>}
        </div>
      </div>

      {/* 칸이 늘어 🗑 가 가려지지 않게 옆으로 밀어 보기(stdTableWrap 은 overflow hidden) */}
      <div style={{ ...stdTableWrap, overflowX: 'auto' }}>
        <table style={colW ? { ...tableStyle, tableLayout: 'fixed' } : tableStyle}>
          {colW && <colgroup>{colW.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>}
          <thead ref={theadRef}>
            <tr>
              <th style={{ ...stdTh, width: 44, textAlign: 'center' }} aria-label="고르기" />
              {COLS.map((c) => <th key={c.key} style={{ ...stdTh, width: c.width, textAlign: c.align || 'left' }}>{c.label}</th>)}
              <th style={{ ...stdTh, width: 44 }} aria-label="삭제" />
            </tr>
          </thead>
          <tbody>
            {active === 'new' ? editor(null, null, close) : (
              <tr ref={addRowRef}>
                <td style={{ ...stdTd, textAlign: 'center', padding: 0 }}>
                  <button type="button" onClick={addNew} title="새 줄 추가" aria-label="새 줄 추가" style={addBtn}>＋</button>
                </td>
                {COLS.map((c) => <td key={c.key} style={stdTd}>{'\u00a0'}</td>)}
                <td style={stdTd} />
              </tr>
            )}
            {rows.length === 0 && active !== 'new' && (
              <tr>
                <td colSpan={COLS.length + 2} style={emptyTd}>
                  {totalCount != null
                    ? '검색에 맞는 줄이 없습니다.'
                    : '매입 행이 없습니다. 맨 위 ＋ 로 바로 쓰거나 [📤 업로드] 의 엑셀/CSV·붙여넣기로 넣어 주세요.'}
                </td>
              </tr>
            )}
            {rows.map((row, rowIdx) => {
              if (active === row.id) return <React.Fragment key={row.id}>{editor(rowColorBg(row), checkbox(row), () => deleteRow(row))}</React.Fragment>;
              const checked = selectedIds.has(row.id);
              const cellBase = { ...stdCell(rowIdx, rows.length), verticalAlign: 'top' };
              const bg = rowColorBg(row) || (checked || row.is_excluded ? 'var(--bg-subtle)' : 'transparent');
              return (
                <tr key={row.id} style={{ background: bg }}>
                  <td style={{ ...cellBase, textAlign: 'center', padding: 0 }}>{checkbox(row)}</td>
                  {COLS.map((c) => (
                    <td
                      key={c.key}
                      title="눌러서 바로 고치기"
                      onClick={(e) => openRow(row.id, row, c.key, e.currentTarget.closest('tr')?.getBoundingClientRect().height || null)}
                      style={{
                        ...cellBase,
                        cursor: 'text',
                        textAlign: c.align || 'left',
                        whiteSpace: c.wrap ? 'normal' : 'nowrap',
                        textDecoration: row.is_excluded ? 'line-through' : 'none',
                        color: row.is_excluded ? 'var(--text-muted)' : 'var(--text)',
                      }}
                    >{c.type === 'money' ? fmtMoney(row[c.key]) : (row[c.key] ?? '')}</td>
                  ))}
                  <td style={{ ...cellBase, textAlign: 'center', padding: 0 }}>
                    <TrashButton onClick={() => deleteRow(row)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const tableStyle = { ...stdTable, minWidth: 1164 };
const emptyTd = { ...stdTd, borderBottom: 'none', padding: '24px', textAlign: 'center', color: 'var(--text-muted)' };
// 늘 같은 높이(44px) — 버튼(🎨·선택 삭제)이 나타나도 표가 밀리지 않는다
const toolbar = {
  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px', minHeight: 44, marginBottom: 8,
  color: 'var(--text-sub)', fontSize: 13,
};
const toolbarRight = { marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 };
// 체크칸 누르는 자리를 칸 전체로 (휴대폰에서 작은 네모만 누르기 어렵다)
const checkLabel = { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 40, cursor: 'pointer' };
// 맨 위 빈 줄의 ＋ (글자 없이 버튼만 — 사용자 5차)
const addBtn = {
  width: 26, height: 26, padding: 0, borderRadius: 8, cursor: 'pointer', lineHeight: '24px',
  border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 16, fontWeight: 700,
};
